import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { expensesApi } from 'src/Services/API';

// The hook walks every page of the year anyway (the operations table filters and sorts
// client-side), so a small page size only multiplies round-trips.
const EXPENSES_PER_PAGE = 100;

const useFetchExpenses = (year?: number) => {
  const assetId = useCurrentAssetId();

  const query = useInfiniteQuery({
    queryKey: ['fetch-expenses', assetId, year],
    queryFn: async ({ pageParam = 1 }) => {
      if (!assetId) throw new Error('No assetId provided');

      return (
        await expensesApi.expensesControllerFindAll(
          assetId,
          pageParam,
          EXPENSES_PER_PAGE,
          undefined,
          undefined,
          year
        )
      ).data;
    },
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;
      return lastPage.currentPage < lastPage.pageCount ? lastPage.currentPage + 1 : undefined;
    },
    enabled: !!assetId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    const loadAllPages = async () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        await query.fetchNextPage();
      }
    };

    loadAllPages();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const allExpenses = useMemo(
    () => query.data?.pages.flatMap(page => page?.items ?? []) ?? [],
    [query.data]
  );

  return {
    data: allExpenses,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    error: query.error,
  };
};
export default useFetchExpenses;
